import React from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import PropTypes from 'prop-types'
import {
  Box,
  colors,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
} from '@material-ui/core'
import { ExternalLink as ExternalLinkIcon } from 'react-feather'

import { CopyInput, FileUploadInput, JoditEditor, Select } from 'components'

import useStyles from './styles'
import theme from 'theme'
import constants from 'constants/index'

const InformationSection = ({ type, currentDocument, renderMode }) => {
  const {
    ACTIVE_STATUS_ID,
    OTHER_CATEGORY_ID,
    CATEGORY_OPTIONS,
    ATTACHMENT_CONTENT_KIND,
    LINK_CONTENT_KIND,
  } = constants.documents

  const { MANAGEMENT_STEP_3, MANAGEMENT_STEP_4 } = constants.tours.documents

  const classes = useStyles()
  const { control, errors, watch } = useFormContext()

  const isShow = type === 'show'

  const category = watch('category')
  const contentKind = watch('contentKind')

  const isActive = currentDocument?.status === ACTIVE_STATUS_ID
  const isAttachmentContent = contentKind === ATTACHMENT_CONTENT_KIND
  const isLinkContent = contentKind === LINK_CONTENT_KIND

  const metadataContent = (
    <Box
      display="flex"
      flexDirection="column"
      gridGap={theme.spacing(3)}
      id={MANAGEMENT_STEP_3}
    >
      {!isShow &&
        !!currentDocument?.generateDocumentLink &&
        currentDocument?.publicDocument &&
        isActive &&
        !isAttachmentContent && (
          <CopyInput
            label="Link de incorporação"
            content={currentDocument?.generateDocumentLink}
            bgcolor={theme.palette.background.default}
            style={{ backgroundColor: colors.blue[50] }}
          />
        )}
      <Controller
        as={
          <Select
            items={CATEGORY_OPTIONS}
            label="Categoria do documento"
            error={!!errors?.category}
            helperText={<>{errors?.category?.message}</>}
            fullWidth
            disabled={isShow}
          />
        }
        control={control}
        name="category"
      />
      {category === OTHER_CATEGORY_ID && (
        <Controller
          as={
            <TextField
              label="Especifique a categoria"
              error={!!errors?.otherCategory}
              helperText={<>{errors?.otherCategory?.message}</>}
              fullWidth
              disabled={isShow}
            />
          }
          control={control}
          name="otherCategory"
        />
      )}
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Controller
            as={
              <TextField
                label="Código"
                error={!!errors?.code}
                helperText={<>{errors?.code?.message}</>}
                fullWidth
                disabled={isShow}
              />
            }
            control={control}
            name="code"
            onChange={([e]) => e.target.value.toUpperCase()}
          />
        </Grid>
      </Grid>
    </Box>
  )

  const editorContent = (
    <Box
      display="flex"
      flexDirection="column"
      component={Paper}
      p={2}
      gridGap={theme.spacing(3)}
      id={MANAGEMENT_STEP_4}
    >
      <Controller
        as={
          <TextField
            variant="standard"
            label="Nome do documento"
            error={!!errors?.title}
            helperText={<>{errors?.title?.message}</>}
            fullWidth
            disabled={isShow}
          />
        }
        control={control}
        name="title"
      />
      <Box
        display="flex"
        flexDirection="column"
        className={isShow && !isLinkContent ? classes.disabledEditor : ''}
      >
        {isLinkContent ? (
          <TextField
            label="Link do documento"
            value={currentDocument?.content || ''}
            disabled
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() =>
                      window.open(currentDocument?.content, '_blank')
                    }
                  >
                    <ExternalLinkIcon size={16} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        ) : isAttachmentContent ? (
          <FileUploadInput
            controlName="attachment"
            accept={constants.validations.FILE_TYPES_DOCS}
            hasDownloadFile
            disabled={isShow}
          />
        ) : (
          <Controller
            control={control}
            name="content"
            as={
              <JoditEditor.Editor
                error={!!errors?.content}
                helperText={errors?.content?.message}
                configRest={{ height: '80vh' }}
                disabled={isShow}
              />
            }
            mode="onChange"
          />
        )}
      </Box>
    </Box>
  )

  if (renderMode === 'editor') {
    return (
      <Box display="flex" flexDirection="column" gridGap={theme.spacing(3)}>
        {editorContent}
      </Box>
    )
  }

  if (renderMode === 'metadata') {
    return metadataContent
  }

  return (
    <Box display="flex" flexDirection="column" gridGap={theme.spacing(3)}>
      {metadataContent}
      {editorContent}
    </Box>
  )
}

InformationSection.propTypes = {
  currentDocument: PropTypes.object,
  renderMode: PropTypes.oneOf(['editor', 'metadata']),
  type: PropTypes.oneOf(['create', 'edit', 'show']).isRequired,
}

export default InformationSection
